import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import PlayerList from './components/PlayerList';
import ScoringSettings from './components/ScoringSettings';
import SettingsSummary from './components/SettingsSummary';
import TournamentTransfer from './components/TournamentTransfer';
import FieldSettings from './components/FieldSettings';
import TeamSettings from './components/TeamSettings';
import RoundSettings from './components/RoundSettings';
import TournamentMatrix from './components/TournamentMatrix';
import MatchSchedule from './components/MatchSchedule';
import Ranking from './components/Ranking';
import HowToGuide from './components/HowToGuide';
import VersionUpdateNotification from './components/VersionUpdateNotification';
import VersionDebugPanel from './components/VersionDebugPanel';
import { useTournament } from './hooks/useTournament';
import { checkForUpdates, getCurrentVersion } from './utils/versionCheck';
import './styles/main.css';

const App = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [waitingSW, setWaitingSW] = useState(null);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isLoading, setIsLoading] = useState(true);
    const [versionInfo, setVersionInfo] = useState({ current: getCurrentVersion(), latest: null });
    const [showVersionNotification, setShowVersionNotification] = useState(false);
    const { optimizing, optimizeProgress } = useTournament();

    // Initialize app loading state
    useEffect(() => {
        // Simulate loading complete after a brief moment
        setTimeout(() => {
            setIsLoading(false);
        }, 100);
    }, []);

    // Register service worker for PWA functionality
    useEffect(() => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/service-worker.js')
                .then(registration => {
                    // Check for updates on registration
                    registration.addEventListener('updatefound', () => {
                        const newWorker = registration.installing;
                        if (newWorker) {
                            newWorker.addEventListener('statechange', () => {
                                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                    // New service worker available
                                    setWaitingSW(newWorker);
                                    setUpdateAvailable(true);
                                }
                            });
                        }
                    });
                })
                .catch(error => {
                    // Service worker registration failed - app will still work without offline support
                });
        }
    }, []);

    // Listen to online/offline events
    useEffect(() => {
        const goOnline = () => setIsOnline(true);
        const goOffline = () => setIsOnline(false);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);

    // Version checking effect
    useEffect(() => {
        const performVersionCheck = async () => {
            await checkForUpdates((currentVersion, latestVersion) => {
                setVersionInfo({ current: currentVersion, latest: latestVersion });
                setShowVersionNotification(true);
            });
        };

        // Initial check after app loads
        if (!isLoading && isOnline) {
            setTimeout(performVersionCheck, 2000); // Wait 2 seconds after app loads
        }

        // Set up periodic checking
        const versionCheckInterval = setInterval(() => {
            if (isOnline) {
                performVersionCheck();
            }
        }, 30 * 60 * 1000); // Check every 30 minutes

        return () => {
            clearInterval(versionCheckInterval);
        };
    }, [isLoading, isOnline]);

    // Check for updates when coming back online
    useEffect(() => {
        if (isOnline && !isLoading) {
            setTimeout(() => {
                checkForUpdates((currentVersion, latestVersion) => {
                    setVersionInfo({ current: currentVersion, latest: latestVersion });
                    setShowVersionNotification(true);
                });
            }, 1000);
        }
    }, [isOnline, isLoading]);

    const handleUpdate = () => {
        if (waitingSW) {
            waitingSW.postMessage({ type: 'SKIP_WAITING' });
            setUpdateAvailable(false);
        }
    };

    // Show loading state initially
    if (isLoading) {
        return React.createElement('div', {
            style: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#f5f5f5',
                fontFamily: 'Arial, sans-serif'
            }
        }, React.createElement('div', {
            style: {
                textAlign: 'center',
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }
        }, [
            React.createElement('h2', { 
                key: 'title',
                style: { color: '#2196F3' } 
            }, 'Tournament Planner'),
            React.createElement('p', { key: 'loading' }, 'Wird geladen...'),
            React.createElement('div', {
                key: 'spinner',
                style: {
                    width: '50px',
                    height: '50px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #2196F3',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto'
                }
            })
        ]));
    }

    return (
        <>
            {!isOnline && (
                <div className="offline-notification">Sie sind offline</div>
            )}
            {/* Version update notification */}
            <VersionUpdateNotification
                currentVersion={versionInfo.current}
                latestVersion={versionInfo.latest}
                show={showVersionNotification}
                onDismiss={() => setShowVersionNotification(false)}
            />
            {updateAvailable && (
                <div className="update-notification">
                    <span>Neue Version verfügbar</span>
                    <button onClick={handleUpdate}>Aktualisieren</button>
                </div>
            )}
            <div className="app-container">
                <Header />
                <HowToGuide />
                <div className="settings-grid two-columns">
                    <div>
                        <PlayerList />
                        <ScoringSettings />
                    </div>
                    <div>
                        {/* Settings: Teams and Rounds first */}
                        <TeamSettings />
                        <RoundSettings />
                        <FieldSettings />
                        <SettingsSummary />
                        <TournamentTransfer />
                    </div>
                </div>
                <TournamentMatrix />
                <MatchSchedule />
                <Ranking />
                <footer className="app-footer">
                    <p>© 2025 Yavuz T. - Urheberrechte</p>
                    <p>spieltagsplaner@outlook.com</p>
                </footer>
                {optimizing && (
                    <div className="loading-overlay">
                        <div className="loading-content">
                            <div className="spinner" />
                            <div className="progress-bar">
                                <div className="progress" style={{ width: `${Math.round(optimizeProgress * 100)}%` }} />
                            </div>
                            <div className="progress-text">Optimierung läuft… {Math.round(optimizeProgress * 100)}%</div>
                        </div>
                    </div>
                )}
            </div>
            {/* Debug panel for testing version checking */}
            <VersionDebugPanel />
        </>
    );
};

export default App;